import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useNavigate } from "react-router";
import { WhatsappTemplateCategoryEnum } from "../../enums";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import ProgressBar from "../../externalComponents/ProgressBar";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import TemplateForm from "../../externalComponents/templateForm";
import { submitTemplate } from "../../externalComponents/templateSubmit";
import { createScenarioFromScratch } from "../../functions/createScenarioFromScratch";
import { CustomerContext } from "../../providers";
import { message } from "antd";

export default function CreateTemplatePage() {
  const navigate = useNavigate();
  const { user, customer, shop } = useContext(CustomerContext);

  const urlParams = new URLSearchParams(window.location.search);

  const campaignId = urlParams.get("campaign") ?? "";
  const campaignName = urlParams.get("campaignName") ?? "";
  const automationId = urlParams.get("automation") ?? "";
  const automationName = urlParams.get("automationName") ?? "";
  const automationType = urlParams.get("automationType") ?? "";

  // Template validation
  const [progression, setProgression] = useState(0);

  const [isButtonSubmitLoading, setIsButtonSubmitLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [activeSubmit, setActiveSumbit] = useState(false);
  const toggleActiveSumbit = useCallback(
    () => setActiveSumbit((active) => !active),
    [],
  );

  const [templateName, setTemplateName] = useState(urlParams.get("name") ?? "");
  // Category
  const [selectedCategory, setSelectedCategory] = useState(
    urlParams.get("category")?.toUpperCase() ??
      WhatsappTemplateCategoryEnum.MARKETING,
  );

  // Header
  const [selectedHeader, setSelectedHeader] = useState([
    urlParams.get("header") ?? "none",
  ]);

  // Limited-time offer
  const [limitedTimeOffer, setLimitedTimeOffer] = useState(
    selectedCategory === "LIMITED_TIME_OFFER"
      ? { text: "", expirationType: "delay" }
      : null,
  );
  const [isLimitedTimeOffer, setIsLimitedTimeOffer] = useState(
    selectedCategory === "LIMITED_TIME_OFFER" ? true : false,
  );

  // Corps
  const [body, setBody] = useState(
    urlParams
      .get("body")
      ?.replaceAll("<br/>", "\n")
      ?.replaceAll("<esp/>", "&") ?? "",
  );

  // Footer
  const [footer, setFooter] = useState(urlParams.get("footer") ?? "");

  // Buttons
  const _numberOfQuickReplies =
    (urlParams.has("reply1") ? 1 : 0) +
    (urlParams.has("reply2") ? 1 : 0) +
    (urlParams.has("reply3") ? 1 : 0);
  const [numberOfQuickReplies, setNumberOfQuickReplies] = useState(
    _numberOfQuickReplies,
  );
  const [numberOfUrl, setNumberOfUrl] = useState(
    urlParams.has("url_button2") ? 2 : urlParams.has("url_button") ? 1 : 0,
  );
  const [copyCodeButton, setCopyCodeButton] = useState(null);
  const [quickReply1, setQuickReply1] = useState(urlParams.get("reply1") ?? "");
  const [quickReply2, setQuickReply2] = useState(urlParams.get("reply2") ?? "");
  const [quickReply3, setQuickReply3] = useState(urlParams.get("reply3") ?? "");
  const [urlButton, setUrlButton] = useState(urlParams.get("url_button") ?? "");
  const _urlButtonLink = urlParams.get("url_button_link");
  const [urlButtonLink, setUrlButtonLink] = useState(
    _urlButtonLink
      ? urlParams.get("url_button_link").includes(`.gadget.app`) ||
        _urlButtonLink.includes("app.getsimio.com")
        ? ""
        : _urlButtonLink?.replaceAll("<esp/>", "&")
      : "",
  );
  const [urlButton2, setUrlButton2] = useState(
    urlParams.get("url_button2") ?? "",
  );
  const _urlButtonLink2 = urlParams.get("url_button_link2");
  const [urlButtonLink2, setUrlButtonLink2] = useState(
    _urlButtonLink2
      ? urlParams
          .get("url_button_link2")
          .includes("getsimio--development.gadget.app") ||
        _urlButtonLink2.includes("app.getsimio.com")
        ? ""
        : _urlButtonLink?.replaceAll("<esp/>", "&")
      : "",
  );

  const [nextPage] = useState(urlParams.get("next_page") ?? "");

  const [isUrlButtonAtBottom, setIsUrlButtonAtBottom] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState(null);
  const [replyButtons, setReplyButtons] = useState([]);

  // Errors
  const [templateNameError, setTemplateNameError] = useState("");
  const [bodyErrorMsg, setBodyErrorMsg] = useState("");
  const [quickReply1ErrorMsg, setQuickReply1ErrorMsg] = useState("");
  const [quickReply2ErrorMsg, setQuickReply2ErrorMsg] = useState("");
  const [quickReply3ErrorMsg, setQuickReply3ErrorMsg] = useState("");
  const [urlButtonErrorMsg, setUrlButtonErrorMsg] = useState("");
  const [urlButtonLinkErrorMsg, setUrlButtonLinkErrorMsg] = useState("");
  const [urlButton2ErrorMsg, setUrlButton2ErrorMsg] = useState("");
  const [urlButtonLink2ErrorMsg, setUrlButtonLink2ErrorMsg] = useState("");
  const [limitedTimeOfferTextError, setLimitedTimeOfferTextError] =
    useState("");
  const [customField1ErrorMsg, setCustomField1ErrorMsg] = useState("");
  const [customField2ErrorMsg, setCustomField2ErrorMsg] = useState("");
  const [customField3ErrorMsg, setCustomField3ErrorMsg] = useState("");
  const [bodyCardErrors, setBodyCardErrors] = useState([]);
  const [buttonCardErrors, setButtonCardErrors] = useState([]);

  const errorSetters = {
    setTemplateNameError,
    setBodyErrorMsg,
    setQuickReply1ErrorMsg,
    setQuickReply2ErrorMsg,
    setQuickReply3ErrorMsg,
    setUrlButtonErrorMsg,
    setUrlButtonLinkErrorMsg,
    setUrlButton2ErrorMsg,
    setUrlButtonLink2ErrorMsg,
    setLimitedTimeOfferTextError,
    setCustomField1ErrorMsg,
    setCustomField2ErrorMsg,
    setCustomField3ErrorMsg,
    setBodyCardErrors,
    setButtonCardErrors,
  };

  const [, setIsSubmitLoading] = useState(false);

  const templateNameRef = useRef(null);
  const bodyRef = useRef(null);
  const quickReply1Ref = useRef(null);
  const quickReply2Ref = useRef(null);
  const quickReply3Ref = useRef(null);
  const urlButtonLinkRef = useRef(null);
  const urlButtonLink2Ref = useRef(null);

  const [pageStatus, setPageStatus] = useState("");

  const customFieldOptions = useMemo(() => {
    if (!user?.customer?.customFields) return [];
    return Object.keys(user.customer.customFields)
      .filter(Boolean)
      .map((fieldKey) => ({
        label: fieldKey,
        value: fieldKey,
      }));
  }, [user?.customer?.customFields]);

  useEffect(() => {
    if (!nextPage) {
      setPageStatus("newCampaign");
    } else if (nextPage === "newTemplate") {
      setPageStatus("insertTemplate");
    } else if (nextPage === "campaigns" && campaignId !== "") {
      setPageStatus("editCampaign");
    } else if (nextPage === "libraryFlow" && automationId !== "") {
      setPageStatus("libraryFlowAutomation");
    } else if (nextPage === "libraryFlow" && campaignId !== "") {
      setPageStatus("libraryFlowCampaign");
    } else if (nextPage && automationId !== "") {
      setPageStatus("editAutomation");
    } else {
      setPageStatus("newAutomation");
    }
  }, [nextPage]);

  useEffect(() => {
    if (urlParams.get("next_page") === "abandoned-cart" && shop) {
      setUrlButton("Je récupère mon panier");
      setUrlButtonLink(`https://${shop?.domain}/${shop?.id}/checkouts/{{1}}`);
      setNumberOfUrl(1);
    }
  }, []);

  const handleCreateCampaign = async (newTemplate, newTemplateName) => {
    setIsCreateLoading(true);

    let nextPagePath = await createScenarioFromScratch({
      customerId: customer.id,
      newTemplate: newTemplate,
      newTemplateName: newTemplateName,
    });
    navigate(nextPagePath);
  };

  return (
    <>
      <PageHeading
        title={
          nextPage === "libraryFlow"
            ? "Customize first message"
            : "Create a template"
        }
        primaryAction="Submit template"
        handlePrimaryAction={async () => {
          setIsButtonSubmitLoading(true);
          try {
            const result = await submitTemplate({
              isInPanel: false,
              templateName,
              body,
              footer,
              copyCodeButton,
              urlButton,
              urlButtonLink,
              urlButton2,
              urlButtonLink2,
              quickReply1,
              quickReply2,
              quickReply3,
              numberOfQuickReplies,
              numberOfUrl,
              replyButtons,
              isUrlButtonAtBottom,
              selectedHeader,
              selectedCategory,
              user,
              setIsSubmitLoading,
              toggleActiveSumbit,
              setProgression,
              setNewTemplateName,
              limitedTimeOffer,
              errorSetters,
            });
            if (result.success) {
              message.success("Template created successfully");
            } else if (result.error) {
              message.error(result.error);
            }
          } finally {
            setIsButtonSubmitLoading(false);
          }
        }}
        isPrimaryDisabled={isButtonSubmitLoading}
        isPrimaryLoading={isButtonSubmitLoading}
      />
      <OverflowFrame>
        <TemplateForm
          templateName={templateName}
          setTemplateName={setTemplateName}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedHeader={selectedHeader}
          setSelectedHeader={setSelectedHeader}
          isLimitedTimeOffer={isLimitedTimeOffer}
          setIsLimitedTimeOffer={setIsLimitedTimeOffer}
          limitedTimeOffer={limitedTimeOffer}
          setLimitedTimeOffer={setLimitedTimeOffer}
          body={body}
          setBody={setBody}
          footer={footer}
          setFooter={setFooter}
          copyCodeButton={copyCodeButton}
          setCopyCodeButton={setCopyCodeButton}
          numberOfQuickReplies={numberOfQuickReplies}
          setNumberOfQuickReplies={setNumberOfQuickReplies}
          quickReply1={quickReply1}
          setQuickReply1={setQuickReply1}
          quickReply2={quickReply2}
          setQuickReply2={setQuickReply2}
          quickReply3={quickReply3}
          setQuickReply3={setQuickReply3}
          urlButton={urlButton}
          urlButton2={urlButton2}
          numberOfUrl={numberOfUrl}
          setNumberOfUrl={setNumberOfUrl}
          setUrlButton={setUrlButton}
          urlButtonLink={urlButtonLink}
          setUrlButtonLink={setUrlButtonLink}
          setUrlButton2={setUrlButton2}
          urlButtonLink2={urlButtonLink2}
          setUrlButtonLink2={setUrlButtonLink2}
          isUrlButtonAtBottom={isUrlButtonAtBottom}
          setIsUrlButtonAtBottom={setIsUrlButtonAtBottom}
          templateNameRef={templateNameRef}
          bodyRef={bodyRef}
          quickReply1Ref={quickReply1Ref}
          quickReply2Ref={quickReply2Ref}
          quickReply3Ref={quickReply3Ref}
          urlButtonLinkRef={urlButtonLinkRef}
          urlButtonLink2Ref={urlButtonLink2Ref}
          isInPanel={false}
          paramOptions={customFieldOptions}
          handleOpenProductPicker={null}
          shopifyUpsellLineItems={[]}
          setShopifyUpsellLineItems={() => {}}
          templateNameError={templateNameError}
          setTemplateNameError={setTemplateNameError}
          bodyErrorMsg={bodyErrorMsg}
          setBodyErrorMsg={setBodyErrorMsg}
          quickReply1ErrorMsg={quickReply1ErrorMsg}
          setQuickReply1ErrorMsg={setQuickReply1ErrorMsg}
          quickReply2ErrorMsg={quickReply2ErrorMsg}
          setQuickReply2ErrorMsg={setQuickReply2ErrorMsg}
          quickReply3ErrorMsg={quickReply3ErrorMsg}
          setQuickReply3ErrorMsg={setQuickReply3ErrorMsg}
          urlButtonErrorMsg={urlButtonErrorMsg}
          setUrlButtonErrorMsg={setUrlButtonErrorMsg}
          urlButtonLinkErrorMsg={urlButtonLinkErrorMsg}
          setUrlButtonLinkErrorMsg={setUrlButtonLinkErrorMsg}
          urlButton2ErrorMsg={urlButton2ErrorMsg}
          setUrlButton2ErrorMsg={setUrlButton2ErrorMsg}
          urlButtonLink2ErrorMsg={urlButtonLink2ErrorMsg}
          setUrlButtonLink2ErrorMsg={setUrlButtonLink2ErrorMsg}
          limitedTimeOfferTextError={limitedTimeOfferTextError}
          setLimitedTimeOfferTextError={setLimitedTimeOfferTextError}
          customField1ErrorMsg={customField1ErrorMsg}
          setCustomField1ErrorMsg={setCustomField1ErrorMsg}
          customField2ErrorMsg={customField2ErrorMsg}
          setCustomField2ErrorMsg={setCustomField2ErrorMsg}
          customField3ErrorMsg={customField3ErrorMsg}
          setCustomField3ErrorMsg={setCustomField3ErrorMsg}
          bodyCardErrors={bodyCardErrors}
          setBodyCardErrors={setBodyCardErrors}
          buttonCardErrors={buttonCardErrors}
          setButtonCardErrors={setButtonCardErrors}
          replyButtons={replyButtons}
          setReplyButtons={setReplyButtons}
        />

        <Dialog
          open={activeSubmit}
          onClose={
            campaignId === "" && automationId === ""
              ? toggleActiveSumbit
              : () => setActiveSumbit(true)
          }
        >
          <DialogTitle close={false}>
            {progression < 100
              ? "The template has been sent successfully"
              : "The template is validated"}
          </DialogTitle>
          <DialogBody>
            {progression < 100 && (
              <>
                <p>
                  <b>{templateName}</b> has been submitted to Meta.
                </p>
                <p>
                  Template validation is underway and usually takes a few
                  seconds.
                </p>
                <br />
                <p>
                  Once the template has been validated, you can use it to{" "}
                  {nextPage === "campaigns" || !nextPage ? (
                    campaignId !== "" ? (
                      <>
                        duplicate the campaign <strong>{campaignName}</strong>
                      </>
                    ) : (
                      "create a campaign or automation"
                    )
                  ) : automationId !== "" ? (
                    <>
                      duplicate the automation <strong>{automationName}</strong>
                    </>
                  ) : (
                    "create an automation"
                  )}
                  .
                </p>
                <br />

                <ProgressBar
                  progress={progression}
                  color="yellow"
                  className="mb-4"
                />
              </>
            )}
            {progression === 100 && (
              <>
                <div className="bg-green-100 p-5 rounded-customBorderRadius">
                  <h2>
                    <strong>{templateName}</strong> has been validated!
                  </h2>
                  <p>You can now use it.</p>
                </div>
              </>
            )}
          </DialogBody>
          <DialogActions>
            {campaignId === "" && automationId === "" ? (
              <Button
                color="secondaryButton"
                onClick={async () => {
                  toggleActiveSumbit();
                  navigate("/templates");
                }}
                disabled={progression < 100}
              >
                OK
              </Button>
            ) : null}
            <Button
              color="primaryButton"
              loading={isCreateLoading}
              onClick={async () => {
                switch (pageStatus) {
                  case "newCampaign":
                    handleCreateCampaign(null, newTemplateName);
                    return;
                  case "editCampaign":
                    navigate(
                      `/campaigns/new/template/?campaign=${campaignId}&campaignName=${campaignName}`,
                    );
                    return;
                  case "editAutomation":
                    navigate(
                      `/automations/new/${nextPage}/?campaign=${automationId}&campaignName=${automationName}`,
                    );
                    return;
                  case "newAutomation":
                    navigate(`/automations/new/${nextPage}`);
                    return;
                  case "libraryFlowCampaign":
                    navigate(
                      `/campaigns/new/template/?next_page=${nextPage}&campaign=${campaignId}&campaignName=${campaignName}`,
                    );
                    return;
                  case "libraryFlowAutomation":
                    navigate(
                      `/automations/new/${automationType}?next_page=${nextPage}&automation=${automationId}&automationName=${automationName}`,
                    );
                    return;
                }
              }}
              disabled={progression < 100}
            >
              {(() => {
                switch (pageStatus) {
                  case "newCampaign":
                    return "Create a campaign";
                  case "insertTemplate":
                    return "Insert template";
                  case "editCampaign":
                    return "Use this template";
                  case "editAutomation":
                    return "Use this template";
                  case "newAutomation":
                    return "Create an automation";
                  case "libraryFlowCampaign":
                    return "Create the campaign flow";
                  case "libraryFlowAutomation":
                    return "Create the automation flow";
                }
              })()}
            </Button>
          </DialogActions>
        </Dialog>
      </OverflowFrame>
    </>
  );
}
